import * as React from "react"
import { cn } from "@/lib/utils"

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6 | HeadingTag | string

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  level?: HeadingLevel
  as?: HeadingTag
}

/**
 * Normalize level to a valid heading tag
 */
const normalizeLevel = (level: HeadingLevel): HeadingTag => {
  if (typeof level === "number") {
    const clamped = Math.max(1, Math.min(6, level))
    return `h${clamped}` as HeadingTag
  }
  if (typeof level === "string") {
    if (/^h[1-6]$/.test(level)) {
      return level as HeadingTag
    }
    const num = parseInt(level, 10)
    if (!isNaN(num) && num >= 1 && num <= 6) {
      return `h${num}` as HeadingTag
    }
  }
  return "h2"
}

/**
 * Simple Heading wrapper component for backward compatibility
 * Prefer using semantic HTML directly: <h1>, <h2>, etc.
 */
export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = 2, as, ...props }, ref) => {
    const normalizedLevel = normalizeLevel(level)
    const Component = (as || normalizedLevel) as any

    return (
      <Component
        ref={ref}
        className={cn("font-semibold font-serif", className)}
        {...props}
      />
    )
  }
)
Heading.displayName = "Heading"
